import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

type GetToken = () => string | null;
type OnRefresh = () => Promise<string | null>;
type OnLogout = () => void;

// Tracks whether a 401 retry has already been attempted for a given request
// to prevent infinite retry loops.
type RetryableConfig = InternalAxiosRequestConfig & {
  _hasRetriedTokenRefresh?: boolean
};

export const applyAuthToInstances = (
  instances: AxiosInstance[],
  getToken: GetToken,
  onRefresh: OnRefresh,
  onLogout: OnLogout,
): (() => void) => {
  const requestInterceptorIds: number[] = [];
  const responseInterceptorIds: number[] = [];

  // Shared across all instances: if a refresh is already in-flight, all
  // concurrent 401s await the same promise instead of each firing their own.
  let refreshPromise: Promise<string | null> | null = null;

  instances.forEach((instance) => {
    const requestId = instance.interceptors.request.use((config) => {
      const token = getToken();
      if (config.requireAuth !== false && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    requestInterceptorIds.push(requestId);

    const responseId = instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error?.config as RetryableConfig | undefined;

        if (
          !axios.isAxiosError(error) ||
          error.response?.status !== 401 ||
          !config ||
          config._hasRetriedTokenRefresh
        ) {
          return Promise.reject(error);
        }

        config._hasRetriedTokenRefresh = true;

        //Make sure that a token refresh hasn't been started before attempting to refresh
        //so that we don't spam the token endpoint if a bunch of requests are made in a short
        //time when a token is expired
        if (!refreshPromise) {
          refreshPromise = onRefresh().finally(() => {
            refreshPromise = null;
          });
        }

        const newToken = await refreshPromise;

        if (!newToken) {
          onLogout();
          return Promise.reject(error);
        }

        config.headers.Authorization = `Bearer ${newToken}`;
        return instance(config);
      },
    );
    responseInterceptorIds.push(responseId);
  });

  return () => {
    instances.forEach((instance, i) => {
      instance.interceptors.request.eject(requestInterceptorIds[i]);
      instance.interceptors.response.eject(responseInterceptorIds[i]);
    });
  };
};
